import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import Sidebar from "./components/Sidebar";
import Home from "./pages/Home";
import Clients from "./pages/Clients";
import Settings from "./pages/Settings";
import ClientDetails from "./pages/ClientDetails";
import RemindersPage from "./pages/RemindersPage";
import useLocalStorage from "use-local-storage";
import "./index.css";
import Finances from "./pages/Finances";
import { useAuth } from "./contexts/AuthContext";
import Account from "./pages/Account";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import HabitAnalysis from "./pages/HabitAnalysis";
import TasksAnalysis from "./pages/TaskAnalysis";
import GoalTracker from "./pages/GoalTracker";
import AdminDashboard from "./pages/AdminDashboard";
import MobileSidebar from "./components/MobileSidebar";
import WorkHoursAnalytics from "./pages/WorkHoursAnalytics";
import MyGrowthPage from "./pages/MyGrowthPage";
import TheBrainDumpPage from "./pages/TheBrainDumpPage";
import WorkTracker from "./pages/WorkTracker";
import Checkout from "./pages/Checkout";
import LandingPage from "./pages/LandingPage";
import Pricing from "./pages/Pricing";
import TermsAndConditions from "./pages/TermsAndConditions";
import TestCheckout from "./pages/TestCheckout";
import TestPayment from "./pages/TestCheckout";
// import UpgradeButton from "./pages/UpgradeButton";
import Growth from "./pages/Growth";
import PaymentSuccess from "./pages/CheckoutSuccess";
import UpgradePage from "./pages/UpgradePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import { Link } from "react-router-dom";

// Protected Route Component (requires authentication)
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" replace />;
};

// PRO Route Component (requires PRO subscription)
const ProRoute = ({ children }) => {
  const { currentUser, subscriptionTier } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has PRO or higher tier (PRO, GOLD)
  const isProOrHigher =
    subscriptionTier === "PRO" || subscriptionTier === "GOLD";

  return isProOrHigher ? children : <Navigate to="/upgrade" replace />;
};

// Employee-only Route Component (requires employee allowlist/role)
const EmployeeRoute = ({ children }) => {
  const {
    currentUser,
    isEmployee,
    employeeAllowlistReady,
    employeeAllowlistError,
  } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Avoid redirecting employees before the allowlist loads.
  if (!employeeAllowlistReady) {
    return <div style={{ padding: "2rem" }}>Checking access…</div>;
  }

  if (!isEmployee) {
    if (employeeAllowlistError) {
      // Helpful for debugging real-user access issues (e.g. rules not deployed)
      console.error("Employee allowlist read error:", employeeAllowlistError);
    }
    return <Navigate to="/app" replace />;
  }

  return children;
};

// Public Route Component (only for non-authenticated users)
const PublicRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return !currentUser ? children : <Navigate to="/app" replace />;
};

// Pro Upgrade Required Page Component
const ProUpgradeRequired = () => {
  return (
    <UpgradeRequiredContainer>
      <UpgradeContent>
        <LockIcon>🔒</LockIcon>
        <h1>PRO Feature</h1>
        <p>This feature requires a PRO subscription.</p>
        <p>Upgrade to unlock this and other premium features.</p>
        <UpgradeButton to="/upgrade">Upgrade to PRO</UpgradeButton>
        <BackButton to="/app">Back to Dashboard</BackButton>
      </UpgradeContent>
    </UpgradeRequiredContainer>
  );
};

function App() {
  const [theme, setTheme] = useLocalStorage("theme", "dark");
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const { currentUser, isAdmin } = useAuth();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 750);

  // Use location to determine layout
  const location = useLocation();
  const isLandingPage = location.pathname === "/";
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/signup";

  // Is the user currently in the "app" section?
  const isInApp = !isLandingPage && !isAuthPage && currentUser;

  const switchTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  useEffect(() => {
    const handleResize = () => {
      const isNarrow = window.innerWidth <= 1300;
      setIsMobileView(window.innerWidth < 750);
      setSidebarWidth(isNarrow ? 0 : 240);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Layout
      data-theme={theme}
      style={{ background: "var(--primaryBackgroundColor)" }}
    >
      {/* Show Sidebar only if logged in AND not on landing/auth pages */}
      {isInApp &&
        (isMobileView ? (
          <MobileSidebar theme={theme} switchTheme={switchTheme} />
        ) : (
          <Sidebar
            theme={theme}
            switchTheme={switchTheme}
            onWidthChange={setSidebarWidth}
          />
        ))}

      <MainContent
        sidebarWidth={sidebarWidth}
        isMobileView={isMobileView}
        loggedIn={isInApp}
      >
        <Routes>
          {/* Public Landing Page */}
          <Route
            path="/"
            element={<LandingPage theme={theme} switchTheme={switchTheme} />}
          />

          {/* Auth routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login theme={theme} />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <Signup theme={theme} />
              </PublicRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <PublicRoute>
                <Pricing theme={theme} />
              </PublicRoute>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={
              <PublicRoute>
                <TermsAndConditions theme={theme} />
              </PublicRoute>
            }
          />

          {/* Upgrade routes */}
          <Route
            path="/upgrade"
            element={
              <ProtectedRoute>
                <UpgradePage theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <SubscriptionPage theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />

          {/* Protected routes - Available to all authenticated users */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Home theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                {isAdmin ? <AdminDashboard /> : <Navigate to="/app" replace />}
              </ProtectedRoute>
            }
          />
          <Route
            path="/habit-analysis"
            element={
              <ProtectedRoute>
                <HabitAnalysis theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-analysis"
            element={
              <ProtectedRoute>
                <TasksAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reminders"
            element={
              <ProtectedRoute>
                <RemindersPage theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account theme={theme} switchTheme={switchTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <Checkout theme={theme} />
              </ProtectedRoute>
            }
          />

          {/* PRO-only routes */}
          <Route
            path="/work-tracker"
            element={
              <EmployeeRoute>
                <WorkTracker theme={theme} switchTheme={switchTheme} />
              </EmployeeRoute>
            }
          />
          <Route
            path="/finances"
            element={
              <ProRoute>
                <Finances theme={theme} switchTheme={switchTheme} />
              </ProRoute>
            }
          />
          <Route
            path="/goals"
            element={
              <ProRoute>
                <GoalTracker theme={theme} switchTheme={switchTheme} />
              </ProRoute>
            }
          />
          <Route
            path="/growth"
            element={
              <ProRoute>
                <Growth theme={theme} switchTheme={switchTheme} />
              </ProRoute>
            }
          />
          <Route
            path="/work-hours"
            element={
              <ProRoute>
                <WorkHoursAnalytics theme={theme} switchTheme={switchTheme} />
              </ProRoute>
            }
          />
          <Route
            path="/brain-dump"
            element={
              <ProRoute>
                <TheBrainDumpPage theme={theme} switchTheme={switchTheme} />
              </ProRoute>
            }
          />

          {/* Catch-all route */}
          <Route
            path="*"
            element={
              currentUser ? (
                <Navigate to="/app" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </MainContent>
    </Layout>
  );
}

export default App;

const Layout = styled.div`
  display: flex;
  padding: 0;
  margin: 0;
  transition: 0.5s;
`;

const MainContent = styled.div`
  /* Only apply sidebar margin if loggedIn prop is true (which means we are in /app) */
  margin-left: ${({ loggedIn, isMobileView, sidebarWidth }) =>
    !loggedIn ? 0 : isMobileView ? 0 : sidebarWidth}px;

  /* Only apply padding if in the app */
  padding: ${({ loggedIn }) => (loggedIn ? "2rem" : "0")};
  width: 100%;
  transition: margin-left 0.3s ease;
  min-height: 100vh;

  @media (max-width: 650px) {
    padding: ${({ loggedIn }) => (loggedIn ? "2em 0em" : "0")};
  }

  @media (max-width: 650px) {
    padding-top: 0px;
  }
`;

const UpgradeRequiredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  padding: 2rem;
  background: var(--primaryBackgroundColor);
`;

const UpgradeContent = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  max-width: 500px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

  h1 {
    color: var(--fontPrimaryColor);
    margin-bottom: 1rem;
    font-size: 2rem;
  }

  p {
    color: var(--fontSecondaryColor);
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const LockIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const UpgradeButton = styled(({ to, children, ...props }) => (
  <LinkButton to={to} {...props}>
    {children}
  </LinkButton>
))`
  display: inline-block;
  background: var(--primaryColor);
  color: white;
  padding: 12px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  margin: 1.5rem 0.5rem 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primaryColorDark);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const BackButton = styled(({ to, children, ...props }) => (
  <LinkButton to={to} {...props}>
    {children}
  </LinkButton>
))`
  display: inline-block;
  background: transparent;
  color: var(--fontSecondaryColor);
  padding: 10px 20px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  margin: 0.5rem;
  border: 1px solid var(--fontSecondaryColor);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--fontPrimaryColor);
  }
`;

// Import Link from react-router-dom at the top of your file

const LinkButton = styled(Link)`
  display: inline-block;
  text-decoration: none;
  font-family: "Montserrat", sans-serif;
  font-size: 0.95rem;
`;
